import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork } from 'app/core/core.module';
import { Part, PartAlias, PartAliasType, PartLink } from 'app/core/model/entity-model';
import { PartSearchComponent } from 'app/parts/part-search/part-search.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

export interface AssemblyRow {
    part: Part;
    quantity: number;
}

export interface ResolvedLine {
    part: Part;
    quantity: number;
}

@Component({
    selector: 'app-assembly-bom-select-dialog',
    templateUrl: './assembly-bom-select-dialog.component.html',
    styleUrls: ['./assembly-bom-select-dialog.component.scss'],
    standalone: false,
})
export class AssemblyBomSelectDialogComponent implements OnInit {
    public modalRef = inject(BsModalRef);
    private uow = inject(GenesisUnitOfWork);
    private modalService = inject(BsModalService);
    private appInfo = inject(AppInformationService);
    private ref = inject(ChangeDetectorRef);

    /** Emitted on confirm/cancel */
    result = new Subject<{ success: boolean; resolvedLines?: ResolvedLine[]; notesSummary?: string }>();

    /** Passed in from caller so alias lookups know the vendor */
    vendorId: number;

    selectedAssemblies: AssemblyRow[] = [];
    resolvedLines: ResolvedLine[] = [];
    notesSummary: string = '';
    calculated = false;
    calculating = false;

    ngOnInit() {}

    async addAssemblyRow() {
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                showSideBar: true,
                storageKey: 'part-search-grid-assemblyBomDialog',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.partIds?.length > 0) {
                const parts = (await this.uow.parts.byIds(result.partIds)) as Part[];
                parts.forEach((part) => {
                    // avoid adding duplicate parents
                    if (!this.selectedAssemblies.find((row) => row.part.id === part.id)) {
                        this.selectedAssemblies = [...this.selectedAssemblies, { part, quantity: 1 }];
                    }
                });
                this.calculated = false;
                this.resolvedLines = [];
                this.notesSummary = '';
                this.ref.detectChanges();
            }
            modalRef.hide();
        });
    }

    removeAssemblyRow(index: number) {
        this.selectedAssemblies = this.selectedAssemblies.filter((_, i) => i !== index);
        this.calculated = false;
        this.resolvedLines = [];
        this.notesSummary = '';
    }

    async calculateLines() {
        if (this.selectedAssemblies.length === 0) return;
        this.calculating = true;
        this.appInfo.pleaseWaitShow();

        /** partId → { part, quantity } accumulated across all selected parents */
        const accumulator = new Map<number, ResolvedLine>();

        for (const row of this.selectedAssemblies) {
            const links = (await this.uow.getPartLinkByPartId(row.part.id)) as PartLink[];

            for (const link of links) {
                const child = link.subPart as Part;
                if (!child) continue;

                const aliases = await this.uow.getPartAliases(child.id);
                const hasVendorAlias = aliases.some(
                    (a: PartAlias) => !a.deleted && a.vendorId === this.vendorId && a.partAliasTypeId === PartAliasType.Types.PurchaseOrder
                );
                if (!hasVendorAlias) continue;

                const qty = link.quantity * row.quantity;
                if (accumulator.has(child.id)) {
                    accumulator.get(child.id).quantity += qty;
                } else {
                    accumulator.set(child.id, { part: child, quantity: qty });
                }
            }
        }

        this.resolvedLines = Array.from(accumulator.values()).sort((a, b) => (a.part.partNumber ?? '').localeCompare(b.part.partNumber ?? ''));

        // Build notes summary using parent parts: "PF-011-24-W Qty: 5, ..."
        const parentSummary = this.selectedAssemblies.map((row) => `${row.part.partNumber} ${row.part.description} Qty: ${row.quantity}`).join('\n');
        this.notesSummary = `Added from BOM:\n${parentSummary}`;

        this.calculated = true;
        this.calculating = false;
        this.appInfo.pleaseWaitHide();
        this.ref.detectChanges();
    }

    confirm() {
        if (!this.calculated || this.resolvedLines.length === 0) return;
        this.result.next({ success: true, resolvedLines: this.resolvedLines, notesSummary: this.notesSummary });
        this.modalRef.hide();
    }

    cancel() {
        this.result.next({ success: false });
        this.modalRef.hide();
    }
}
